import _ from 'lodash';
import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import DisabledMixin from './mixins/DisabledMixin';
import FocusMixin from './mixins/FocusMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import * as postMessage from '../privates/postMessage';
import platformUtils from '../privates/platformUtils';
import imageUtils from '../privates/utils/imageUtils';
import videoUtils from '../privates/utils/videoUtils';
import audioUtils from '../privates/utils/audioUtils';
import documentUtils from '../privates/utils/documentUtils';
import {validate} from '../privates/validations/validation';
import {emptyStringIfNil} from '../privates/utils';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';
import {runValidationsAndSync, hideValidityIndication} from '../privates/validationsService/validationsService';
import RequiredMixin from './mixins/RequiredMixin';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';

/**
 * @typedef {external:Object} $w.UploadButton~File
 * @summary The object used by the `value` property that represents
 *  files ready for upload.
 * @property {external:String} name The file's name.
 * @property {external:String} size The file's size in bytes.
 * @snippet [UploadButton-value.es6=Get the files pending upload]
 * @see [value](#value)
 */

/**
 * @typedef {external:Object} $w.UploadButton~UploadedFile
 * @summary The object returned by the `startUpload()` function's
 *  Promise that contains the URL of the successfully uploaded file.
 * @property {String} url The Wix URL of the uploaded file.
 * @property {String} mediaId The Wix media ID of the uploaded file.
 * @property {String} title The title of the uploaded file.
 * @property {Number} width The width of an uploaded image file.
 * @property {Number} height The height of an uploaded image file.
 * @snippet [UploadButton-startUpload.es6=Get uploaded file information]
 * @see [startUpload](#startUpload)
 */

/**
 * @typedef {external:Object} $w.UploadButton~UploadError
 * @summary The object returned by the `startUpload()` function's
 *  Promise when an upload fails.
 * @property {external:String} errorCode Numeric The error's code.
 * @property {external:String} errorDescription The error's description.
 * @snippet [UploadButton-startUpload.es6=Get upload error information]
 * @see [startUpload](#startUpload)
 */


/**
 * @class UploadButton
 * @summary An upload button enables users to upload files to your site.
 * @description
 *  <h3 id="typical-scenario">Typical File Upload Scenario</h3>
 * 
 *  In a typical scenario, the page from which files are uploaded contains
 *  an upload button and another element, such as a regular button. The user chooses
 *  which file to upload by clicking the upload button and selecting the
 *  file in a native file chooser dialog. At that point the file is stored in
 *  the upload button's [`value`](#value) property as a [`File`](#File) object. Then
 *  the user triggers an event, such as a button click, on the other element.
 *  That element's event handler calls the [`startUpload()`](#startUpload)
 *  function to perform the actual upload. The upload either succeeds and
 *  gives you an [`UploadedFile`](#UploadedFile) object, or fails and gives you
 *  an [`UploadError`](#UploadError) object.
 * @snippet [UploadButton-startUpload_scenario.es6=Typical file upload scenario]
 *  This example uploads a file when the user clicks on a button. First it
 *  checks to see if the user has chosen a file. If a file was chosen, it
 *  triggers the upload and logs the status of the upload when it completes
 *  successfully or with an error.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.FocusMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.FileUploader
 * @definitionId wysiwyg.viewer.components.inputs.FileUploader
 * @tagname fileuploader
 */
class UploadButton extends FormElement {

    // PROPERTIES:

    /**
     * @member buttonLabel
     * @label property
     * @syntax
     * get buttonLabel(): string
     * set buttonLabel(value: string): void
     * @summary Sets or gets the label on the upload button.
     * @type {external:String}
     * @snippet [UploadButton-buttonLabel_get.es6=Get an upload button's label]
     * @snippet [UploadButton-buttonLabel_set.es6=Set an upload button's label]
     * @memberof $w.UploadButton
     * @instance
     */
    get buttonLabel() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);

        return compData && compData.buttonLabel;
    }


  /**
   * @member buttonLabel
   * @memberof $w.UploadButton
   * @param {external:String} label
   */
    set buttonLabel(label) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'buttonLabel',
            dataToValidate: {buttonLabel: label},
            compName: this.id
        })) {
            return;
        }
        RMI.setData(compId, {
            buttonLabel: emptyStringIfNil(label)
        });
    }

    /**
     * @member fileType
     * @label property
     * @syntax
     * get fileType(): FileType
     * set fileType(value: FileType): void
     * type FileType = "Image" | "Document" | "Video" | "Audio"
     * @summary Sets or gets the type of file that a user can upload.
     * @description
     *  Setting the `fileType` property sets the type of file that
     *  a user can upload. The value of `fileType` can be set to either
     *  `"Image"`, `"Document"`, `"Video"`, or `"Audio"`.
     *
     *  Getting the `fileType` property returns the current type of
     *  file that a user can upload.
     *
     *  The file types of the images and documents that can be uploaded are
     *  listed [here](https://support.wix.com/en/article/supported-media-file-types-and-file-sizes).
     * @snippet [UploadButton-fileType_get.es6=Get an upload button's type]
     * @snippet [UploadButton-fileType_set.es6=Set an upload button's type]
     * @type {external:String}
     * @memberof $w.UploadButton
     */
    get fileType() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compProps = RMI.getProps(compId);

        return compProps && compProps.filesType;
    }

  /**
   * @member fileType
   * @memberof $w.UploadButton
   * @param {external:String} type
   */
    set fileType(type) {
        type = _.isString(type) ? _.startCase(type) : type;
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'fileType',
            dataToValidate: {filesType: type},
            shouldCheckMandatoryFields: true,
            compName: this.id
        })) {
            return;
        }
        RMI.setProps(compId, {
            filesType: type
        });


    }

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): File[]
     * @summary Returns a list of files that are pending upload.
     * @description
     *  Returns a list of [File](#File) objects that a user has selected to
     *  upload after pressing the upload button.
     *
     *  See how `value` is used in a
     *  [typical file upload scenario](#typical-scenario).
     * @snippet [UploadButton-value.es6=Get the files pending upload]
     * @snippet [UploadButton-startUpload_scenario.es6=Full file upload scenario]
     *  This example uploads a file when the user clicks on a button. First it
     *  checks to see if the user has chosen a file. If a file was chosen, it
     *  triggers the upload and logs the status of the upload when it completes
     *  successfully or with an error.
     * @type {$w.UploadButton~File[]}
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compState = RMI.getState(compId);

        return _.get(compState, 'value');
    }

    /**
     * @member value
     * @memberof $w.UploadButton
     * @param {$w.UploadButton~File[]} value
     * @override
     */
    set value(val) { // eslint-disable-line no-unused-vars
        const compType = members._type.get(this).compType;
        reporter.reportSdkError(generateError.error_only_getter({
            propertyName: 'value',
            compType
        }));
    }

    /**
     * @function startUpload
     * @syntax
     * function startUpload(): Promise<UploadedFile>
     * @summary Uploads the files that the user has chosen.
     * @description
     *  The `startUpload()` function triggers the upload of the files in the
     *  upload button's [value](#value) property.
     *
     *  See how `startUpload()` is used in a
     *  [typical file upload scenario](#typical-scenario).
     *
     *  The upload button does not accept files that are larger than 25MB.
     * @returns {Promise}
     * @fulfill {$w.UploadButton~UploadedFile} Resolves when the file upload is completed.
     * @reject {$w.UploadButton~UploadError} Rejects if the file upload fails.
     * @snippet [UploadButton-startUpload.es6=Start a file upload]
     * @snippet [UploadButton-startUpload_scenario.es6=Full file upload scenario]
     *  This example uploads a file when the user clicks on a button. First it
     *  checks to see if the user has chosen a file. If a file was chosen, it
     *  triggers the upload and logs the status of the upload when it completes
     *  successfully or with an error.
     * @memberof $w.UploadButton
     */
    startUpload() {
        const compId = members._compId.get(this);
        return new Promise((resolve, reject) => {
            const callback = (data, err) => {
                if (err) {
                    reject(err);
                } else {
                    const uriUtils = platformUtils.getUriUtils();
                    const mediaType = data.media_type;
                    let url;
                    switch (mediaType) {
                      case 'picture':
                        url = uriUtils.generateWixCodeImageUri(data);
                        break;
                      case 'document':
                        url = uriUtils.generateWixCodeDocumentUri(data);
                        break;
                      case 'video':
                        url = uriUtils.generateWixCodeVideoUri(data);
                        break;
                      case 'music':
                        url = uriUtils.generateWixCodeAudioUri(data);
                        break;
                      default:
                        resolve(data);
                        return;
                    }
                    if (!platformUtils.isExperimentOpen('bv_mediaIdInUploadButton')) {
                        resolve({url});
                        return;
                    }
                    switch (mediaType) {
                        case 'picture':
                          const { uri, title, width, height } = imageUtils.extractModelDataFromSrc(url);
                          resolve({ url, mediaId: uri, title, width, height });
                          break;
                        case 'document':
                          const documentData = documentUtils.extractModelDataFromSrc(url);
                          resolve({url, mediaId: documentData.mediaId, title: documentData.title});
                          break;
                        case 'video':
                          const {videoId, posterImageRef, fileName} = videoUtils.extractModelDataFromSrc(url);
                          resolve({url, mediaId: videoId, title: fileName, width: posterImageRef.width, height: posterImageRef.height});
                          break;
                        case 'music':
                          const audioData = audioUtils.extractModelDataFromSrc(url);
                          resolve({url, mediaId: audioData.mediaId, title: audioData.title, duration: audioData.duration});
                          break;
                        default:
                          resolve(data);
                          return;
                      }
                }
            };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.START_UPLOAD,
                data: {compId},
                controllerId: members._controllerId.get(this),
                callback
            });
        });
    }

    /**
     * @function reset
     * @syntax
     * function reset(): void
     * @summary Clears the files that are pending upload.
     * @description
     *  The `value` of the upload button contains a list of files that have been
     *  selected to upload. The `reset()` function clears these files.
     * @snippet [UploadButton-reset.es6=Clears the files pending upload]
     * @memberof $w.UploadButton
     */
    reset() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const value = [];
        const newState = _.defaults({value, uploadStatus: 'NOT_STARTED', serverMessage: ''}, RMI.getState(compId));

        RMI.updateModel({[compId]: {state: newState}});
        RMI.executeCompBehavior(compId, 'reset');
        runValidationsAndSync(RMI, compId, value); // must run validations after executing rest behavior so that viewer validations will be correct
        hideValidityIndication(RMI, compId);
    }

    toJSON() {
        const {id, type, global, rendered, required, value, buttonLabel, fileType, startUpload, reset} = this;
        return {id, type, global, rendered, required, value, buttonLabel, fileType, startUpload, reset};
    }

    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the upload button's styles.
     * @description
     *  The following styles can be used with upload buttons:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting an upload button's styles, gets or sets the styles of the
     *  upload button's regular state. It does not set the styles of the upload button's hover,
     *  focus, error, or disabled states.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */
}

mixin(UploadButton.prototype, HiddenCollapsedMixin.prototype);
mixin(UploadButton.prototype, DisabledMixin.prototype);
mixin(UploadButton.prototype, FocusMixin.prototype);
mixin(UploadButton.prototype, StyleMixin.prototype);
mixin(UploadButton.prototype, RequiredMixin.prototype);

setStageUnfriendlyFunctions(UploadButton, 'startUpload');
export default UploadButton;
